// 将 G6 图模型转换为 STN-Runtime 诊断服务的数据契约。
//
// 前端图模型（见 topology.js / TopologyGraph.vue）：
//   node: { id, label, category, status?, meta? }
//   edge: { source, target, linkId?, linkType?, label?, value?, status?, meta? }
// 后端契约（见 STN-Runtime/server：三层 nodes / edges / flows，后端再展开 NIC）：
//   node: { id, type, props, routes? }
//   edge: { id, source, target, type, props, source_nic?, target_nic? }
//   flow: { id, source, destination, path_nodes, path_channels, props }
//
// 前端只产出 nodes / edges（无 flows 层），NIC 拆分、channel.connects/carries
// 回填、节点 interfaces 回填全部由后端完成。status 取值（normal/saturated/
// degraded/down）两端一致，直接透传。

// 前端拓扑类型 -> 后端 topology_type 标签（仅作元数据）
const TOPOLOGY_TYPE_MAP = {
  drone: 'drone_network',
  communication: 'communication_network',
  microservice: 'microservice',
  machinery: 'complex_machinery'
}

// 丢弃 undefined / null / 空字符串，保留 0 和 false。
function cleanProps(obj) {
  const out = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === '') continue
    out[k] = v
  }
  return out
}

// meta 是 { 中文键: 值 } 字典，原样并入 props——它是诊断智能体的推理依据。
function flattenMeta(meta) {
  if (!meta || typeof meta !== 'object') return {}
  const out = {}
  for (const [k, v] of Object.entries(meta)) {
    if (v === undefined || v === null || v === '') continue
    out[k] = v
  }
  return out
}

function toNode(n) {
  const node = {
    id: String(n.id),
    type: n.category || '',
    props: cleanProps({
      label: n.label,
      category: n.category,
      status: n.status || 'normal',
      ...flattenMeta(n.meta)
    })
  }
  // 透传路由表（若前端/LLM 提供）：[{destination_nodes, egress_interface, next_hop}]。
  // 后端无法从拓扑推导路由，仅原样带过去。
  if (Array.isArray(n.routes) && n.routes.length) node.routes = n.routes
  return node
}

// 为缺少 linkId（信道编号）的边生成稳定且唯一的 channel id。
function makeEdgeIdFactory() {
  const used = new Set()
  return (edge, index) => {
    let base = String(edge.linkId || edge.id || `E_${edge.source}_${edge.target}`)
    let id = base
    let suffix = index
    while (used.has(id)) id = `${base}_${suffix++}`
    used.add(id)
    return id
  }
}

// 把前端边端点的网卡（sourceNic/targetNic）转成后端 NIC 结构 { id?, props }。
// 接口指标可放在 nic.meta（中文键，与节点/边一致）或 nic.props；两者都并入 props，
// 由后端 graph_to_twin 规范化成 canonical 键（tx_packets/tx_rate_mbps/… ）。
function toNic(nic) {
  if (!nic || typeof nic !== 'object') return undefined
  const props = cleanProps({ status: nic.status, ...flattenMeta(nic.props), ...flattenMeta(nic.meta) })
  const id = nic.id ? String(nic.id) : ''
  if (!id && Object.keys(props).length === 0) return undefined
  const out = { props }
  if (id) out.id = id
  return out
}

function toEdge(e, id) {
  const edge = {
    id,
    source: String(e.source),
    target: String(e.target),
    type: e.linkType || '',
    props: cleanProps({
      label: e.label,
      status: e.status || 'normal',
      link_type: e.linkType,
      // 前端 value 约等于容量/吞吐（Mbps，见 TopologyGraph.vue 注释）
      capacity_mbps: typeof e.value === 'number' ? e.value : undefined,
      ...flattenMeta(e.meta)
    })
  }
  // 透传两端网卡（若前端/LLM 提供）；缺失时后端会自动合成并从信道指标灌值。
  const sourceNic = toNic(e.sourceNic)
  const targetNic = toNic(e.targetNic)
  if (sourceNic) edge.source_nic = sourceNic
  if (targetNic) edge.target_nic = targetNic
  return edge
}

// 明确属于"端到端两端"的节点分类：这类节点总是数据流的端点，
// 即使它在图里度数大于 1（例如挂了多条链路的终端）。
const ENDPOINT_CATEGORIES = new Set([
  'terminal',
  'sensor',
  'actuator',
  'storage'
])

// 单次转换最多推导多少条流，避免端点很多时 C(n,2) 组合爆炸。
const MAX_DERIVED_FLOWS = 50

// 用后端形态的 edges（含最终 channel id）建无向邻接表。
// node -> [{ to: 邻居节点id, channel: 信道id }]
function buildAdjacency(nodes, edges) {
  const adj = new Map()
  for (const n of nodes) adj.set(String(n.id), [])
  for (const e of edges) {
    const s = String(e.source)
    const t = String(e.target)
    if (!adj.has(s) || !adj.has(t)) continue
    adj.get(s).push({ to: t, channel: e.id })
    adj.get(t).push({ to: s, channel: e.id })
  }
  return adj
}

// BFS 最短路：返回 { path_nodes, path_channels }；不连通返回 null。
function shortestPath(adj, src, dst) {
  if (src === dst) return null
  const prev = new Map() // node -> { from, channel }
  const visited = new Set([src])
  const queue = [src]
  while (queue.length) {
    const cur = queue.shift()
    if (cur === dst) break
    for (const { to, channel } of adj.get(cur) || []) {
      if (visited.has(to)) continue
      visited.add(to)
      prev.set(to, { from: cur, channel })
      queue.push(to)
    }
  }
  if (!visited.has(dst)) return null
  const pathNodes = [dst]
  const pathChannels = []
  let cur = dst
  while (cur !== src) {
    const step = prev.get(cur)
    pathChannels.unshift(step.channel)
    cur = step.from
    pathNodes.unshift(cur)
  }
  return { path_nodes: pathNodes, path_channels: pathChannels }
}

/**
 * 从后端形态的 nodes / edges 推导端到端候选数据流。
 *
 * 端点 = 度为 1 的叶子节点 ∪ 终端类分类节点；对每一对端点取无向最短路，
 * 生成一条流。这类流没有真实流量测量（demand/throughput 未知），只提供
 * 路径拓扑，让诊断智能体能把"某条信道饱和/退化"关联到"哪些端到端路径受影响"。
 *
 * @param {Array<{id:string,type?:string}>} nodes 后端形态节点
 * @param {Array<{id:string,source:string,target:string}>} edges 后端形态边（含最终 channel id）
 * @returns {Array} data_flow 层数组（可能为空）
 */
export function deriveFlowsFromEdges(nodes, edges) {
  if (!Array.isArray(nodes) || !Array.isArray(edges) || !edges.length) return []
  const adj = buildAdjacency(nodes, edges)

  // 端点：叶子（度为 1） 或 终端类分类。去重后按 id 排序，保证确定性。
  const endpoints = nodes
    .map((n) => String(n.id))
    .filter((id) => {
      const degree = (adj.get(id) || []).length
      const category = String(
        (nodes.find((n) => String(n.id) === id) || {}).type || ''
      )
      return degree === 1 || ENDPOINT_CATEGORIES.has(category)
    })
    .sort()

  const flows = []
  let seq = 1
  // 遍历每一对端点（无序对），取最短路作为一条流。
  for (let i = 0; i < endpoints.length && flows.length < MAX_DERIVED_FLOWS; i++) {
    for (let j = i + 1; j < endpoints.length; j++) {
      if (flows.length >= MAX_DERIVED_FLOWS) break
      const path = shortestPath(adj, endpoints[i], endpoints[j])
      if (!path) continue // 不连通
      flows.push({
        id: `F${String(seq++).padStart(4, '0')}`,
        source: endpoints[i],
        destination: endpoints[j],
        path_nodes: path.path_nodes,
        path_channels: path.path_channels,
        // 拓扑推导流没有真实测量值，props 留空；后端信道 carries 的
        // bandwidth 会默认为 0，不臆造 demand/throughput 以免误导诊断。
        props: {}
      })
    }
  }
  return flows
}

/**
 * 把 G6 图数据（graph.save() 或原始 { nodes, edges }）转换为诊断请求体。
 *
 * @param {{nodes?: Array, edges?: Array}} g6Data 当前图模型（含用户编辑）
 * @param {Object} [options]
 * @param {string} [options.sceneName] 场景名（后端会规范化为库名）；默认用拓扑类型
 * @param {string} [options.topologyType] 前端拓扑类型：drone/communication/...
 * @param {string} [options.question] 诊断问题；省略则后端用默认分析问题
 * @param {string} [options.taskType] analysis | evolution | optimization
 * @param {Array}  [options.flows] 显式数据流层；提供时直接透传，不做推导
 * @param {boolean} [options.deriveFlows] 未提供 flows 时是否从拓扑推导，默认 true
 * @returns {Object} 诊断服务的 POST /api/diagnose 请求体
 */
export function graphToContract(g6Data, options = {}) {
  const data = g6Data || {}
  const rawNodes = Array.isArray(data.nodes) ? data.nodes : []
  const rawEdges = Array.isArray(data.edges) ? data.edges : []

  if (!rawNodes.length) {
    throw new Error('拓扑图为空：至少需要一个节点才能诊断。')
  }

  const nodeIds = new Set(rawNodes.map((n) => String(n.id)))
  const nextEdgeId = makeEdgeIdFactory()

  const nodes = rawNodes.map(toNode)
  const edges = rawEdges
    // 只保留两端都存在的边，避免后端因引用未知节点而 400。
    .filter((e) => nodeIds.has(String(e.source)) && nodeIds.has(String(e.target)))
    .map((e, i) => toEdge(e, nextEdgeId(e, i)))

  const topologyType = options.topologyType || ''
  const sceneName =
    options.sceneName || `${topologyType || 'scene'}_topology`

  // flows 优先用调用方显式提供的；否则从拓扑推导。传 deriveFlows:false 可关闭推导。
  let flows = Array.isArray(options.flows) ? options.flows : Array.isArray(data.flows) ? data.flows : []
  if (!flows.length && options.deriveFlows !== false) {
    flows = deriveFlowsFromEdges(nodes, edges)
  }

  const body = {
    scene_name: sceneName,
    topology_type: TOPOLOGY_TYPE_MAP[topologyType] || topologyType || undefined,
    graph: {
      nodes,
      edges,
      flows
    }
  }
  if (options.question) body.question = options.question
  if (options.taskType) body.task_type = options.taskType
  return body
}

export { TOPOLOGY_TYPE_MAP }

